import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import {
  attritionRateDefinition,
  attritionRateHandler,
} from '../definitions/attritionRate';
import { companyDefinition, companyHandler } from '../definitions/company';
import {
  competitorsDefinition,
  competitorsHandler,
} from '../definitions/competitors';
import {
  contingentWorkersDefinition,
  contingentWorkersHandler,
} from '../definitions/contingentWorkers';
import {
  customSkillDefinition,
  customSkillHandler,
} from '../definitions/customSkill';
import {
  dataAdjustmentsDefinition,
  dataAdjustmentsHandler,
} from '../definitions/dataAdjustments';
import {
  dateRangeDefinition,
  dateRangeHandler,
} from '../definitions/dateRange';
import {
  educationDefinition,
  educationHandler,
} from '../definitions/education';
import {
  ethnicityDefinition,
  ethnicityHandler,
} from '../definitions/ethnicity';
import {
  externallyOwnedDefinition,
  externallyOwnedHandler,
} from '../definitions/externallyOwned';
import {
  flightRiskDefinition,
  flightRiskHandler,
} from '../definitions/flightRisk';
import {
  founderEthnicityDefinition,
  founderEthnicityHandler,
} from '../definitions/founderEthnicity';
import {
  founderGenderDefinition,
  founderGenderHandler,
} from '../definitions/founderGender';
import {
  founderInfoDefinition,
  founderInfoHandler,
} from '../definitions/founderInfo';
import {
  founderPreviousCompanyDefinition,
  founderPreviousCompanyHandler,
} from '../definitions/founderPreviousCompany';
import {
  founderPreviousSchoolDefinition,
  founderPreviousSchoolHandler,
} from '../definitions/founderPreviousSchool';
import {
  fundingInfoDefinition,
  fundingInfoHandler,
} from '../definitions/fundingInfo';
import {
  fundingStageDefinition,
  fundingStageHandler,
} from '../definitions/fundingStage';
import { genderDefinition, genderHandler } from '../definitions/gender';
import {
  geographyDefinition,
  geographyHandler,
} from '../definitions/geography';
import {
  graduationYearDefinition,
  graduationYearHandler,
} from '../definitions/graduationYear';
import {
  growthRateDefinition,
  growthRateHandler,
} from '../definitions/growthRate';
import {
  headcountDefinition,
  headcountHandler,
} from '../definitions/headcount';
import {
  hiringRateDefinition,
  hiringRateHandler,
} from '../definitions/hiringRate';
import {
  hqLocationDefinition,
  hqLocationHandler,
} from '../definitions/hqLocation';
import { industryDefinition, industryHandler } from '../definitions/industry';
import { inflowDefinition, inflowHandler } from '../definitions/inflow';
import { keywordsDefinition, keywordsHandler } from '../definitions/keywords';
import {
  lastFundingAmountDefinition,
  lastFundingAmountHandler,
} from '../definitions/lastFundingAmount';
import {
  lastFundingYearDefinition,
  lastFundingYearHandler,
} from '../definitions/lastFundingYear';
import {
  metricModeDefinition,
  metricModeHandler,
} from '../definitions/metricMode';
import {
  multiAttritionRateDefinition,
  multiAttritionRateHandler,
} from '../definitions/multi-attritionRate';
import {
  multiCompanyDefinition,
  multiCompanyHandler,
} from '../definitions/multi-company';
import {
  multiCustomSkillDefinition,
  multiCustomSkillHandler,
} from '../definitions/multi-customSkill';
import {
  multiEducationDefinition,
  multiEducationHandler,
} from '../definitions/multi-education';
import {
  multiEthnicityDefinition,
  multiEthnicityHandler,
} from '../definitions/multi-ethnicity';
import {
  multiFlightRiskDefinition,
  multiFlightRiskHandler,
} from '../definitions/multi-flightRisk';
import {
  multiGenderDefinition,
  multiGenderHandler,
} from '../definitions/multi-gender';
import {
  multiGeographyDefinition,
  multiGeographyHandler,
} from '../definitions/multi-geography';
import {
  multiGraduationYearDefinition,
  multiGraduationYearHandler,
} from '../definitions/multi-graduationYear';
import {
  multiGrowthRateDefinition,
  multiGrowthRateHandler,
} from '../definitions/multi-growthRate';
import {
  multiHeadcountDefinition,
  multiHeadcountHandler,
} from '../definitions/multi-headcount';
import {
  multiHiringRateDefinition,
  multiHiringRateHandler,
} from '../definitions/multi-hiringRate';
import {
  multiInflowDefinition,
  multiInflowHandler,
} from '../definitions/multi-inflow';
import {
  multiKeywordsDefinition,
  multiKeywordsHandler,
} from '../definitions/multi-keywords';
import {
  multiOutflowDefinition,
  multiOutflowHandler,
} from '../definitions/multi-outflow';
import { multiPayDefinition, multiPayHandler } from '../definitions/multi-pay';
import {
  multiPrestigeDefinition,
  multiPrestigeHandler,
} from '../definitions/multi-prestige';
import {
  multiRemoteSuitabilityDefinition,
  multiRemoteSuitabilityHandler,
} from '../definitions/multi-remoteSuitability';
import {
  multiRoleDefinition,
  multiRoleHandler,
} from '../definitions/multi-role';
import {
  multiSalaryDefinition,
  multiSalaryHandler,
} from '../definitions/multi-salary';
import {
  multiSchoolDefinition,
  multiSchoolHandler,
} from '../definitions/multi-school';
import {
  multiSeniorityDefinition,
  multiSeniorityHandler,
} from '../definitions/multi-seniority';
import {
  multiTenureDefinition,
  multiTenureHandler,
} from '../definitions/multi-tenure';
import {
  multiTitleDefinition,
  multiTitleHandler,
} from '../definitions/multi-title';
import {
  multiYearsOfExperienceDefinition,
  multiYearsOfExperienceHandler,
} from '../definitions/multi-yearsOfExperience';
import { nameDefinition, nameHandler } from '../definitions/name';
import { outflowDefinition, outflowHandler } from '../definitions/outflow';
import {
  overallRatingDefinition,
  overallRatingHandler,
} from '../definitions/overallRating';
import { payDefinition, payHandler } from '../definitions/pay';
import {
  postingsSourceDefinition,
  postingsSourceHandler,
} from '../definitions/postingsSource';
import { prestigeDefinition, prestigeHandler } from '../definitions/prestige';
import { publicDefinition, publicHandler } from '../definitions/public';
import {
  remoteSuitabilityDefinition,
  remoteSuitabilityHandler,
} from '../definitions/remoteSuitability';
import { roleDefinition, roleHandler } from '../definitions/role';
import {
  roleTaxonomyDefinition,
  roleTaxonomyHandler,
} from '../definitions/roleTaxonomy';
import { salaryDefinition, salaryHandler } from '../definitions/salary';
import { schoolDefinition, schoolHandler } from '../definitions/school';
import {
  seniorityDefinition,
  seniorityHandler,
} from '../definitions/seniority';
import { skillDefinition, skillHandler } from '../definitions/skill';
import {
  type CustomSkillTaxonomySelection,
  skillTaxonomyDefinition,
  skillTaxonomyHandler,
} from '../definitions/skillTaxonomy';
import {
  snapshotDateDefinition,
  snapshotDateHandler,
} from '../definitions/snapshotDate';
import { tenureDefinition, tenureHandler } from '../definitions/tenure';
import { titleDefinition, titleHandler } from '../definitions/title';
import {
  totalFundingAmountDefinition,
  totalFundingAmountHandler,
} from '../definitions/totalFundingAmount';
import {
  valuationDefinition,
  valuationHandler,
} from '../definitions/valuation';
import {
  yearFoundedDefinition,
  yearFoundedHandler,
} from '../definitions/yearFounded';
import {
  yearsOfExperienceDefinition,
  yearsOfExperienceHandler,
} from '../definitions/yearsOfExperience';
import {
  type DateFilter,
  type DateRangeFilter,
  type Filter,
  FilterId,
  type FreeTextFilter,
  type GroupFilter,
  type ListFilter,
  type MultiFilter,
  type RangeFilter,
  type SchoolFilter,
  type SegmentedFilter,
  type SingleValueFilter,
  type StaticListFilter,
  type TreeFilter,
} from '../types/filters';
import type { FilterDefinition, FilterHandler } from './types';

export interface AllFilterDefinitionsMapType {
  [FilterId.COMPANY]: FilterDefinition<TreeFilter>;
  [FilterId.DATA_ADJUSTMENTS]: FilterDefinition<StaticListFilter>;
  [FilterId.DATE_RANGE]: FilterDefinition<DateRangeFilter>;
  [FilterId.EDUCATION]: FilterDefinition<ListFilter>;
  [FilterId.ETHNICITY]: FilterDefinition<ListFilter>;
  [FilterId.FLIGHT_RISK]: FilterDefinition<StaticListFilter>;
  [FilterId.GENDER]: FilterDefinition<ListFilter>;
  [FilterId.GEOGRAPHY]: FilterDefinition<TreeFilter>;
  [FilterId.INDUSTRY]: FilterDefinition<TreeFilter>;
  [FilterId.KEYWORDS]: FilterDefinition<FreeTextFilter>;
  [FilterId.NAME]: FilterDefinition<FreeTextFilter>;
  [FilterId.OVERALL_RATING]: FilterDefinition<StaticListFilter>;
  [FilterId.POSTINGS_SOURCE]: FilterDefinition<StaticListFilter>;
  [FilterId.METRIC_MODE]: FilterDefinition<StaticListFilter>;
  [FilterId.PRESTIGE]: FilterDefinition<StaticListFilter>;
  [FilterId.REMOTE_SUITABILITY]: FilterDefinition<StaticListFilter>;
  [FilterId.ROLE]: FilterDefinition<TreeFilter>;
  [FilterId.ROLE_TAXONOMY]: FilterDefinition<
    SingleValueFilter<CustomRoleTaxonomySelection>
  >;
  [FilterId.SCHOOL]: FilterDefinition<SchoolFilter>;
  [FilterId.SKILL_TAXONOMY]: FilterDefinition<
    SingleValueFilter<CustomSkillTaxonomySelection>
  >;
  [FilterId.SENIORITY]: FilterDefinition<ListFilter>;
  [FilterId.SKILL]: FilterDefinition<TreeFilter>;
  [FilterId.CUSTOM_SKILL]: FilterDefinition<TreeFilter>;
  [FilterId.SNAPSHOT_DATE]: FilterDefinition<DateFilter>;
  [FilterId.TITLE]: FilterDefinition<FreeTextFilter>;
  [FilterId.MULTI_TITLE]: FilterDefinition<MultiFilter<FreeTextFilter>>;
  [FilterId.MULTI_KEYWORDS]: FilterDefinition<MultiFilter<FreeTextFilter>>;
  [FilterId.MULTI_CUSTOM_SKILL]: FilterDefinition<MultiFilter<TreeFilter>>;
  [FilterId.MULTI_COMPANY]: FilterDefinition<MultiFilter<TreeFilter>>;
  [FilterId.MULTI_GEOGRAPHY]: FilterDefinition<MultiFilter<TreeFilter>>;
  [FilterId.MULTI_ROLE]: FilterDefinition<MultiFilter<TreeFilter>>;
  [FilterId.MULTI_FLIGHT_RISK]: FilterDefinition<MultiFilter<StaticListFilter>>;
  [FilterId.MULTI_PRESTIGE]: FilterDefinition<MultiFilter<StaticListFilter>>;
  [FilterId.MULTI_REMOTE_SUITABILITY]: FilterDefinition<
    MultiFilter<StaticListFilter>
  >;
  [FilterId.MULTI_SENIORITY]: FilterDefinition<MultiFilter<ListFilter>>;
  [FilterId.MULTI_PAY]: FilterDefinition<MultiFilter<RangeFilter>>;
  [FilterId.MULTI_GENDER]: FilterDefinition<MultiFilter<ListFilter>>;
  [FilterId.MULTI_ETHNICITY]: FilterDefinition<MultiFilter<ListFilter>>;
  [FilterId.MULTI_EDUCATION]: FilterDefinition<MultiFilter<ListFilter>>;
  [FilterId.MULTI_GRADUATION_YEAR]: FilterDefinition<MultiFilter<RangeFilter>>;
  [FilterId.MULTI_YEARS_OF_EXPERIENCE]: FilterDefinition<
    MultiFilter<RangeFilter>
  >;
  [FilterId.MULTI_SCHOOL]: FilterDefinition<MultiFilter<SchoolFilter>>;
  [FilterId.CONTINGENT_WORKERS]: FilterDefinition<StaticListFilter>;
  [FilterId.GRADUATION_YEAR]: FilterDefinition<RangeFilter>;
  [FilterId.PAY]: FilterDefinition<RangeFilter>;
  [FilterId.YEARS_OF_EXPERIENCE]: FilterDefinition<RangeFilter>;
  // Screener-specific filters
  [FilterId.ATTRITION_RATE]: FilterDefinition<RangeFilter>;
  [FilterId.GROWTH_RATE]: FilterDefinition<RangeFilter>;
  [FilterId.HIRING_RATE]: FilterDefinition<RangeFilter>;
  [FilterId.HEADCOUNT]: FilterDefinition<RangeFilter>;
  [FilterId.INFLOW]: FilterDefinition<RangeFilter>;
  [FilterId.OUTFLOW]: FilterDefinition<RangeFilter>;
  [FilterId.SALARY]: FilterDefinition<RangeFilter>;
  [FilterId.TENURE]: FilterDefinition<RangeFilter>;
  [FilterId.YEAR_FOUNDED]: FilterDefinition<RangeFilter>;
  [FilterId.COMPETITORS]: FilterDefinition<TreeFilter>;
  [FilterId.HQ_LOCATION]: FilterDefinition<TreeFilter>;
  [FilterId.EXTERNALLY_OWNED]: FilterDefinition<StaticListFilter>;
  [FilterId.PUBLIC]: FilterDefinition<StaticListFilter>;
  [FilterId.FOUNDER_INFO]: FilterDefinition<GroupFilter>;
  [FilterId.FOUNDER_ETHNICITY]: FilterDefinition<ListFilter>;
  [FilterId.FOUNDER_GENDER]: FilterDefinition<ListFilter>;
  [FilterId.FOUNDER_COMPANY]: FilterDefinition<TreeFilter>;
  [FilterId.FOUNDER_PREVIOUS_SCHOOL]: FilterDefinition<SchoolFilter>;
  [FilterId.FUNDING_INFO]: FilterDefinition<GroupFilter>;
  [FilterId.FUNDING_STAGE]: FilterDefinition<StaticListFilter>;
  [FilterId.LAST_FUNDING_AMOUNT]: FilterDefinition<RangeFilter>;
  [FilterId.LAST_FUNDING_YEAR]: FilterDefinition<RangeFilter>;
  [FilterId.TOTAL_FUNDING_AMOUNT]: FilterDefinition<RangeFilter>;
  [FilterId.VALUATION]: FilterDefinition<RangeFilter>;

  [FilterId.MULTI_ATTRITION_RATE]: FilterDefinition<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_GROWTH_RATE]: FilterDefinition<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_HIRING_RATE]: FilterDefinition<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_HEADCOUNT]: FilterDefinition<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_INFLOW]: FilterDefinition<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_OUTFLOW]: FilterDefinition<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_SALARY]: FilterDefinition<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_TENURE]: FilterDefinition<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
}

export const ALL_FILTER_DEFINITIONS: Readonly<AllFilterDefinitionsMapType> = {
  [FilterId.COMPANY]: companyDefinition,
  [FilterId.DATA_ADJUSTMENTS]: dataAdjustmentsDefinition,
  [FilterId.DATE_RANGE]: dateRangeDefinition,
  [FilterId.EDUCATION]: educationDefinition,
  [FilterId.ETHNICITY]: ethnicityDefinition,
  [FilterId.FLIGHT_RISK]: flightRiskDefinition,
  [FilterId.GENDER]: genderDefinition,
  [FilterId.GEOGRAPHY]: geographyDefinition,
  [FilterId.INDUSTRY]: industryDefinition,
  [FilterId.KEYWORDS]: keywordsDefinition,
  [FilterId.NAME]: nameDefinition,
  [FilterId.OVERALL_RATING]: overallRatingDefinition,
  [FilterId.POSTINGS_SOURCE]: postingsSourceDefinition,
  [FilterId.METRIC_MODE]: metricModeDefinition,
  [FilterId.PRESTIGE]: prestigeDefinition,
  [FilterId.REMOTE_SUITABILITY]: remoteSuitabilityDefinition,
  [FilterId.ROLE]: roleDefinition,
  [FilterId.ROLE_TAXONOMY]: roleTaxonomyDefinition,
  [FilterId.SCHOOL]: schoolDefinition,
  [FilterId.SKILL_TAXONOMY]: skillTaxonomyDefinition,
  [FilterId.SENIORITY]: seniorityDefinition,
  [FilterId.SKILL]: skillDefinition,
  [FilterId.CUSTOM_SKILL]: customSkillDefinition,
  [FilterId.SNAPSHOT_DATE]: snapshotDateDefinition,
  [FilterId.TITLE]: titleDefinition,
  [FilterId.MULTI_TITLE]: multiTitleDefinition,
  [FilterId.MULTI_KEYWORDS]: multiKeywordsDefinition,
  [FilterId.MULTI_CUSTOM_SKILL]: multiCustomSkillDefinition,
  [FilterId.MULTI_COMPANY]: multiCompanyDefinition,
  [FilterId.MULTI_GEOGRAPHY]: multiGeographyDefinition,
  [FilterId.MULTI_ROLE]: multiRoleDefinition,
  [FilterId.MULTI_FLIGHT_RISK]: multiFlightRiskDefinition,
  [FilterId.MULTI_PRESTIGE]: multiPrestigeDefinition,
  [FilterId.MULTI_REMOTE_SUITABILITY]: multiRemoteSuitabilityDefinition,
  [FilterId.MULTI_SENIORITY]: multiSeniorityDefinition,
  [FilterId.MULTI_PAY]: multiPayDefinition,
  [FilterId.MULTI_GENDER]: multiGenderDefinition,
  [FilterId.MULTI_ETHNICITY]: multiEthnicityDefinition,
  [FilterId.MULTI_EDUCATION]: multiEducationDefinition,
  [FilterId.MULTI_GRADUATION_YEAR]: multiGraduationYearDefinition,
  [FilterId.MULTI_YEARS_OF_EXPERIENCE]: multiYearsOfExperienceDefinition,
  [FilterId.MULTI_SCHOOL]: multiSchoolDefinition,
  [FilterId.CONTINGENT_WORKERS]: contingentWorkersDefinition,
  [FilterId.GRADUATION_YEAR]: graduationYearDefinition,
  [FilterId.PAY]: payDefinition,
  [FilterId.YEARS_OF_EXPERIENCE]: yearsOfExperienceDefinition,
  // Screener-specific filters
  [FilterId.ATTRITION_RATE]: attritionRateDefinition,
  [FilterId.GROWTH_RATE]: growthRateDefinition,
  [FilterId.HIRING_RATE]: hiringRateDefinition,
  [FilterId.HEADCOUNT]: headcountDefinition,
  [FilterId.INFLOW]: inflowDefinition,
  [FilterId.OUTFLOW]: outflowDefinition,
  [FilterId.SALARY]: salaryDefinition,
  [FilterId.TENURE]: tenureDefinition,
  [FilterId.YEAR_FOUNDED]: yearFoundedDefinition,
  [FilterId.COMPETITORS]: competitorsDefinition,
  [FilterId.HQ_LOCATION]: hqLocationDefinition,
  [FilterId.EXTERNALLY_OWNED]: externallyOwnedDefinition,
  [FilterId.PUBLIC]: publicDefinition,
  [FilterId.FOUNDER_INFO]: founderInfoDefinition,
  [FilterId.FOUNDER_ETHNICITY]: founderEthnicityDefinition,
  [FilterId.FOUNDER_GENDER]: founderGenderDefinition,
  [FilterId.FOUNDER_COMPANY]: founderPreviousCompanyDefinition,
  [FilterId.FOUNDER_PREVIOUS_SCHOOL]: founderPreviousSchoolDefinition,
  [FilterId.FUNDING_INFO]: fundingInfoDefinition,
  [FilterId.FUNDING_STAGE]: fundingStageDefinition,
  [FilterId.LAST_FUNDING_AMOUNT]: lastFundingAmountDefinition,
  [FilterId.LAST_FUNDING_YEAR]: lastFundingYearDefinition,
  [FilterId.TOTAL_FUNDING_AMOUNT]: totalFundingAmountDefinition,
  [FilterId.VALUATION]: valuationDefinition,

  [FilterId.MULTI_ATTRITION_RATE]: multiAttritionRateDefinition,
  [FilterId.MULTI_GROWTH_RATE]: multiGrowthRateDefinition,
  [FilterId.MULTI_HIRING_RATE]: multiHiringRateDefinition,
  [FilterId.MULTI_HEADCOUNT]: multiHeadcountDefinition,
  [FilterId.MULTI_INFLOW]: multiInflowDefinition,
  [FilterId.MULTI_OUTFLOW]: multiOutflowDefinition,
  [FilterId.MULTI_SALARY]: multiSalaryDefinition,
  [FilterId.MULTI_TENURE]: multiTenureDefinition,
};

export const SUB_FILTER_IDS = new Set([
  FilterId.MULTI_ATTRITION_RATE,
  FilterId.MULTI_GROWTH_RATE,
  FilterId.MULTI_HIRING_RATE,
  FilterId.MULTI_HEADCOUNT,
  FilterId.MULTI_INFLOW,
  FilterId.MULTI_OUTFLOW,
  FilterId.MULTI_SALARY,
  FilterId.MULTI_TENURE,
]);

/**
 * Base screener filter IDs that should only exist as wrapped filters inside their MULTI counterparts.
 * These filters have definitions in ALL_FILTER_DEFINITIONS (used as templates for wrapped filters),
 * but should never appear in effectiveFilters as standalone filters in screener v2.
 * They are excluded from effectiveFilters in useEffectiveFilters hook.
 */
export const WRAPPED_ONLY_FILTER_IDS = new Set([
  FilterId.ATTRITION_RATE,
  FilterId.GROWTH_RATE,
  FilterId.HIRING_RATE,
  FilterId.HEADCOUNT,
  FilterId.INFLOW,
  FilterId.OUTFLOW,
  FilterId.SALARY,
  FilterId.TENURE,
]);

export const FILTER_FORMAT_MAP: Partial<
  Record<FilterId, { text: string; type: 'prefix' | 'suffix' | 'none' }>
> = {
  [FilterId.ATTRITION_RATE]: { text: '%', type: 'suffix' },
  [FilterId.GROWTH_RATE]: { text: '%', type: 'suffix' },
  [FilterId.HIRING_RATE]: { text: '%', type: 'suffix' },
  [FilterId.HEADCOUNT]: { text: '', type: 'none' },
  [FilterId.INFLOW]: { text: '', type: 'none' },
  [FilterId.OUTFLOW]: { text: '', type: 'none' },
  [FilterId.SALARY]: { text: '$', type: 'prefix' },
  [FilterId.TENURE]: { text: 'yr', type: 'suffix' },
  [FilterId.MULTI_ATTRITION_RATE]: { text: '%', type: 'suffix' },
  [FilterId.MULTI_GROWTH_RATE]: { text: '%', type: 'suffix' },
  [FilterId.MULTI_HIRING_RATE]: { text: '%', type: 'suffix' },
  [FilterId.MULTI_HEADCOUNT]: { text: '', type: 'none' },
  [FilterId.MULTI_INFLOW]: { text: '', type: 'none' },
  [FilterId.MULTI_OUTFLOW]: { text: '', type: 'none' },
  [FilterId.MULTI_SALARY]: { text: '$', type: 'prefix' },
  [FilterId.MULTI_TENURE]: { text: 'yr', type: 'suffix' },
  [FilterId.LAST_FUNDING_AMOUNT]: { text: '$', type: 'prefix' },
  [FilterId.TOTAL_FUNDING_AMOUNT]: { text: '$', type: 'prefix' },
  [FilterId.VALUATION]: { text: '$', type: 'prefix' },
};

export const DEFAULT_ACTIVE_FILTERS = Object.values(
  ALL_FILTER_DEFINITIONS
).filter((definition) => definition.isActive);

export interface AllFilterHandlersMapType {
  [FilterId.COMPANY]: FilterHandler<TreeFilter>;
  [FilterId.DATA_ADJUSTMENTS]: FilterHandler<StaticListFilter>;
  [FilterId.DATE_RANGE]: FilterHandler<DateRangeFilter>;
  [FilterId.EDUCATION]: FilterHandler<ListFilter>;
  [FilterId.ETHNICITY]: FilterHandler<ListFilter>;
  [FilterId.FLIGHT_RISK]: FilterHandler<StaticListFilter>;
  [FilterId.GENDER]: FilterHandler<ListFilter>;
  [FilterId.GEOGRAPHY]: FilterHandler<TreeFilter>;
  [FilterId.INDUSTRY]: FilterHandler<TreeFilter>;
  [FilterId.KEYWORDS]: FilterHandler<FreeTextFilter>;
  [FilterId.NAME]: FilterHandler<FreeTextFilter>;
  [FilterId.OVERALL_RATING]: FilterHandler<StaticListFilter>;
  [FilterId.POSTINGS_SOURCE]: FilterHandler<StaticListFilter>;
  [FilterId.METRIC_MODE]: FilterHandler<StaticListFilter>;
  [FilterId.PRESTIGE]: FilterHandler<StaticListFilter>;
  [FilterId.REMOTE_SUITABILITY]: FilterHandler<StaticListFilter>;
  [FilterId.ROLE]: FilterHandler<TreeFilter>;
  [FilterId.ROLE_TAXONOMY]: FilterHandler<
    SingleValueFilter<CustomRoleTaxonomySelection>
  >;
  [FilterId.SCHOOL]: FilterHandler<SchoolFilter>;
  [FilterId.SKILL_TAXONOMY]: FilterHandler<
    SingleValueFilter<CustomSkillTaxonomySelection>
  >;
  [FilterId.SENIORITY]: FilterHandler<ListFilter>;
  [FilterId.SKILL]: FilterHandler<TreeFilter>;
  [FilterId.CUSTOM_SKILL]: FilterHandler<TreeFilter>;
  [FilterId.SNAPSHOT_DATE]: FilterHandler<DateFilter>;
  [FilterId.TITLE]: FilterHandler<FreeTextFilter>;
  // Wrapped-only handlers (used when these filters appear as segmented)
  [FilterId.ATTRITION_RATE]: FilterHandler<RangeFilter>;
  [FilterId.GROWTH_RATE]: FilterHandler<RangeFilter>;
  [FilterId.HIRING_RATE]: FilterHandler<RangeFilter>;
  [FilterId.HEADCOUNT]: FilterHandler<RangeFilter>;
  [FilterId.INFLOW]: FilterHandler<RangeFilter>;
  [FilterId.OUTFLOW]: FilterHandler<RangeFilter>;
  [FilterId.SALARY]: FilterHandler<RangeFilter>;
  [FilterId.TENURE]: FilterHandler<RangeFilter>;
  [FilterId.MULTI_TITLE]: FilterHandler<MultiFilter<FreeTextFilter>>;
  [FilterId.MULTI_KEYWORDS]: FilterHandler<MultiFilter<FreeTextFilter>>;
  [FilterId.MULTI_CUSTOM_SKILL]: FilterHandler<MultiFilter<TreeFilter>>;
  [FilterId.MULTI_COMPANY]: FilterHandler<MultiFilter<TreeFilter>>;
  [FilterId.MULTI_GEOGRAPHY]: FilterHandler<MultiFilter<TreeFilter>>;
  [FilterId.MULTI_ROLE]: FilterHandler<MultiFilter<TreeFilter>>;
  [FilterId.MULTI_FLIGHT_RISK]: FilterHandler<MultiFilter<StaticListFilter>>;
  [FilterId.MULTI_PRESTIGE]: FilterHandler<MultiFilter<StaticListFilter>>;
  [FilterId.MULTI_REMOTE_SUITABILITY]: FilterHandler<
    MultiFilter<StaticListFilter>
  >;
  [FilterId.MULTI_SENIORITY]: FilterHandler<MultiFilter<ListFilter>>;
  [FilterId.MULTI_PAY]: FilterHandler<MultiFilter<RangeFilter>>;
  [FilterId.MULTI_GENDER]: FilterHandler<MultiFilter<ListFilter>>;
  [FilterId.MULTI_ETHNICITY]: FilterHandler<MultiFilter<ListFilter>>;
  [FilterId.MULTI_EDUCATION]: FilterHandler<MultiFilter<ListFilter>>;
  [FilterId.MULTI_GRADUATION_YEAR]: FilterHandler<MultiFilter<RangeFilter>>;
  [FilterId.MULTI_YEARS_OF_EXPERIENCE]: FilterHandler<MultiFilter<RangeFilter>>;
  [FilterId.MULTI_SCHOOL]: FilterHandler<MultiFilter<SchoolFilter>>;
  [FilterId.CONTINGENT_WORKERS]: FilterHandler<StaticListFilter>;
  [FilterId.GRADUATION_YEAR]: FilterHandler<RangeFilter>;
  [FilterId.PAY]: FilterHandler<RangeFilter>;
  [FilterId.YEARS_OF_EXPERIENCE]: FilterHandler<RangeFilter>;
  [FilterId.YEAR_FOUNDED]: FilterHandler<RangeFilter>;
  [FilterId.COMPETITORS]: FilterHandler<TreeFilter>;
  [FilterId.HQ_LOCATION]: FilterHandler<TreeFilter>;
  [FilterId.EXTERNALLY_OWNED]: FilterHandler<StaticListFilter>;
  [FilterId.PUBLIC]: FilterHandler<StaticListFilter>;
  [FilterId.FOUNDER_INFO]: FilterHandler<GroupFilter>;
  [FilterId.FOUNDER_ETHNICITY]: FilterHandler<ListFilter>;
  [FilterId.FOUNDER_GENDER]: FilterHandler<ListFilter>;
  [FilterId.FOUNDER_COMPANY]: FilterHandler<TreeFilter>;
  [FilterId.FOUNDER_PREVIOUS_SCHOOL]: FilterHandler<SchoolFilter>;
  [FilterId.FUNDING_INFO]: FilterHandler<GroupFilter>;
  [FilterId.FUNDING_STAGE]: FilterHandler<StaticListFilter>;
  [FilterId.LAST_FUNDING_AMOUNT]: FilterHandler<RangeFilter>;
  [FilterId.LAST_FUNDING_YEAR]: FilterHandler<RangeFilter>;
  [FilterId.TOTAL_FUNDING_AMOUNT]: FilterHandler<RangeFilter>;
  [FilterId.VALUATION]: FilterHandler<RangeFilter>;

  [FilterId.MULTI_ATTRITION_RATE]: FilterHandler<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_GROWTH_RATE]: FilterHandler<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_HIRING_RATE]: FilterHandler<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_HEADCOUNT]: FilterHandler<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_INFLOW]: FilterHandler<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_OUTFLOW]: FilterHandler<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_SALARY]: FilterHandler<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
  [FilterId.MULTI_TENURE]: FilterHandler<
    MultiFilter<SegmentedFilter<RangeFilter>>
  >;
}

const ALL_FILTER_HANDLERS: Readonly<AllFilterHandlersMapType> = {
  [FilterId.COMPANY]: companyHandler,
  [FilterId.DATA_ADJUSTMENTS]: dataAdjustmentsHandler,
  [FilterId.DATE_RANGE]: dateRangeHandler,
  [FilterId.EDUCATION]: educationHandler,
  [FilterId.ETHNICITY]: ethnicityHandler,
  [FilterId.FLIGHT_RISK]: flightRiskHandler,
  [FilterId.GENDER]: genderHandler,
  [FilterId.GEOGRAPHY]: geographyHandler,
  [FilterId.INDUSTRY]: industryHandler,
  [FilterId.KEYWORDS]: keywordsHandler,
  [FilterId.NAME]: nameHandler,
  [FilterId.OVERALL_RATING]: overallRatingHandler,
  [FilterId.POSTINGS_SOURCE]: postingsSourceHandler,
  [FilterId.METRIC_MODE]: metricModeHandler,
  [FilterId.PRESTIGE]: prestigeHandler,
  [FilterId.REMOTE_SUITABILITY]: remoteSuitabilityHandler,
  [FilterId.ROLE]: roleHandler,
  [FilterId.ROLE_TAXONOMY]: roleTaxonomyHandler,
  [FilterId.SCHOOL]: schoolHandler,
  [FilterId.SKILL_TAXONOMY]: skillTaxonomyHandler,
  [FilterId.SENIORITY]: seniorityHandler,
  [FilterId.SKILL]: skillHandler,
  [FilterId.CUSTOM_SKILL]: customSkillHandler,
  [FilterId.SNAPSHOT_DATE]: snapshotDateHandler,
  // Wrapped-only handlers (used when these filters appear as segmented)
  [FilterId.ATTRITION_RATE]: attritionRateHandler,
  [FilterId.GROWTH_RATE]: growthRateHandler,
  [FilterId.HIRING_RATE]: hiringRateHandler,
  [FilterId.HEADCOUNT]: headcountHandler,
  [FilterId.INFLOW]: inflowHandler,
  [FilterId.OUTFLOW]: outflowHandler,
  [FilterId.SALARY]: salaryHandler,
  [FilterId.TENURE]: tenureHandler,
  [FilterId.TITLE]: titleHandler,
  [FilterId.MULTI_TITLE]: multiTitleHandler,
  [FilterId.MULTI_KEYWORDS]: multiKeywordsHandler,
  [FilterId.MULTI_CUSTOM_SKILL]: multiCustomSkillHandler,
  [FilterId.MULTI_COMPANY]: multiCompanyHandler,
  [FilterId.MULTI_GEOGRAPHY]: multiGeographyHandler,
  [FilterId.MULTI_ROLE]: multiRoleHandler,
  [FilterId.MULTI_FLIGHT_RISK]: multiFlightRiskHandler,
  [FilterId.MULTI_PRESTIGE]: multiPrestigeHandler,
  [FilterId.MULTI_REMOTE_SUITABILITY]: multiRemoteSuitabilityHandler,
  [FilterId.MULTI_SENIORITY]: multiSeniorityHandler,
  [FilterId.MULTI_PAY]: multiPayHandler,
  [FilterId.MULTI_GENDER]: multiGenderHandler,
  [FilterId.MULTI_ETHNICITY]: multiEthnicityHandler,
  [FilterId.MULTI_EDUCATION]: multiEducationHandler,
  [FilterId.MULTI_GRADUATION_YEAR]: multiGraduationYearHandler,
  [FilterId.MULTI_YEARS_OF_EXPERIENCE]: multiYearsOfExperienceHandler,
  [FilterId.MULTI_SCHOOL]: multiSchoolHandler,

  [FilterId.CONTINGENT_WORKERS]: contingentWorkersHandler,
  [FilterId.GRADUATION_YEAR]: graduationYearHandler,
  [FilterId.PAY]: payHandler,
  [FilterId.YEARS_OF_EXPERIENCE]: yearsOfExperienceHandler,
  [FilterId.YEAR_FOUNDED]: yearFoundedHandler,
  [FilterId.COMPETITORS]: competitorsHandler,
  [FilterId.HQ_LOCATION]: hqLocationHandler,
  [FilterId.EXTERNALLY_OWNED]: externallyOwnedHandler,
  [FilterId.PUBLIC]: publicHandler,
  [FilterId.FOUNDER_INFO]: founderInfoHandler,
  [FilterId.FOUNDER_ETHNICITY]: founderEthnicityHandler,
  [FilterId.FOUNDER_GENDER]: founderGenderHandler,
  [FilterId.FOUNDER_COMPANY]: founderPreviousCompanyHandler,
  [FilterId.FOUNDER_PREVIOUS_SCHOOL]: founderPreviousSchoolHandler,
  [FilterId.FUNDING_INFO]: fundingInfoHandler,
  [FilterId.FUNDING_STAGE]: fundingStageHandler,
  [FilterId.LAST_FUNDING_AMOUNT]: lastFundingAmountHandler,
  [FilterId.LAST_FUNDING_YEAR]: lastFundingYearHandler,
  [FilterId.TOTAL_FUNDING_AMOUNT]: totalFundingAmountHandler,
  [FilterId.VALUATION]: valuationHandler,

  [FilterId.MULTI_ATTRITION_RATE]: multiAttritionRateHandler,
  [FilterId.MULTI_GROWTH_RATE]: multiGrowthRateHandler,
  [FilterId.MULTI_HIRING_RATE]: multiHiringRateHandler,
  [FilterId.MULTI_HEADCOUNT]: multiHeadcountHandler,
  [FilterId.MULTI_INFLOW]: multiInflowHandler,
  [FilterId.MULTI_OUTFLOW]: multiOutflowHandler,
  [FilterId.MULTI_SALARY]: multiSalaryHandler,
  [FilterId.MULTI_TENURE]: multiTenureHandler,
};

export function findFilterById<T extends FilterId>(
  filters: Filter[],
  filterId: T
): AllFilterDefinitionsMapType[T] | undefined {
  return filters.find(
    (filter): filter is AllFilterDefinitionsMapType[T] => filter.id === filterId
  );
}

export function getFilterDefinition<T extends FilterId>(
  filterId: T
): AllFilterDefinitionsMapType[T] | undefined {
  return ALL_FILTER_DEFINITIONS[
    filterId as keyof typeof ALL_FILTER_DEFINITIONS
  ] as AllFilterDefinitionsMapType[T] | undefined;
}

export function getFilterHandler<T extends FilterId>(
  filterId: T
):
  | (T extends keyof AllFilterHandlersMapType
      ? AllFilterHandlersMapType[T]
      : undefined)
  | undefined {
  return ALL_FILTER_HANDLERS[filterId as keyof typeof ALL_FILTER_HANDLERS] as
    | (T extends keyof AllFilterHandlersMapType
        ? AllFilterHandlersMapType[T]
        : undefined)
    | undefined;
}

// Type assertions to ensure all FilterId keys are present in ALL_FILTERS_DEFINITIONS and ALL_FILTER_HANDLERS
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ensureAllDefinitions: AllFilterDefinitionsMapType =
  ALL_FILTER_DEFINITIONS;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ensureAllHandlersMatchDefinition: AllFilterHandlersMapType =
  ALL_FILTER_HANDLERS;
